import { CALL_API, getJSON } from 'redux-api-middleware';
import { Map, fromJS } from 'immutable';
import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import { createSelector } from 'reselect';
import { keys } from 'lodash';
import { API_ROOT, API_REQUEST_EMPTY_ACTION, getDefaultRequestHeaders,
  getDefaultAPIRequestErrorFSA, getStateCountryEntities,
  getStatePlayerPositionEntities, getStatePlayerEntities,
  getSelectedSeason, getSelectedLeagues, getSelectedTeams,
  getStateSeasonEntities, getStateLeagueEntities, getStateTeamEntities
  } from '../../core/duck';
import schemas from '../../core/schemas';

// Constants

const SCOUT_REPORT_REQUEST = 'SCOUT_REPORT_REQUEST';
const SCOUT_REPORT_SUCCESS = 'SCOUT_REPORT_SUCCESS';
const SCOUT_REPORT_REORDER_GRID = 'SCOUT_REPORT_REORDER_GRID';
const SCOUT_REPORT_ADD_PLAYER = 'SCOUT_REPORT_ADD_PLAYER';
const SCOUT_REPORT_ADD_PLAYER_SUCCESS = 'SCOUT_REPORT_ADD_PLAYER_SUCCESS';
const SCOUT_REPORT_REMOVE_PLAYER = 'SCOUT_REPORT_REMOVE_PLAYER';
const SCOUT_REPORT_CHANGE_CONFIGURATION = 'SCOUT_REPORT_CHANGE_CONFIGURATION';
const SCOUT_REPORT_CHANGE_CONTENT = 'SCOUT_REPORT_CHANGE_CONTENT';
const SCOUT_REPORT_CHANGE_PLAYER_CONTENT = 'SCOUT_REPORT_CHANGE_PLAYER_CONTENT';
const SCOUT_REPORT_CHANGE_PLAYER_CONFIGURATION = 'SCOUT_REPORT_CHANGE_PLAYER_CONFIGURATION';
const TEAM_PLAYERS_JERSEYS_SUCCESS = 'TEAM_PLAYERS_JERSEYS_SUCCESS';

// Action creators

export const fetchScoutReport = (seasonId, leagueId, teamId) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/seasons/${seasonId}/leagues/${leagueId}/teams/${teamId}/scout_report/`,
    method: 'POST',
    headers: getDefaultRequestHeaders(),
    types: [
      {
        type: SCOUT_REPORT_REQUEST
      },
      {
        type: SCOUT_REPORT_SUCCESS,
        payload: (action, state, res) => {
          return getJSON(res)
            .then((json) => {
              const camelizedJson = camelizeKeys(json);
              let normalized = normalize(camelizedJson, schemas.SCOUT_REPORT);
              let scoutReport = normalized.entities.scoutReports[normalized.result];
              delete normalized.entities.scoutReports;

              scoutReport.players = [];

              if (normalized.entities.scoutReportPlayers) {
                keys(normalized.entities.scoutReportPlayers).map(rowId => {
                  const row = normalized.entities.scoutReportPlayers[rowId];
                  normalized.entities.players[row.player]['content'] = row.content;
                  scoutReport.players.push({
                    playerId: row.player,
                    playerConfiguration: row.playerConfiguration
                  });
                  return true;
                });
              }
              delete normalized.entities.scoutReportPlayers;

              return {
                entities: normalized.entities,
                data: scoutReport
              };
            });
        }
      },
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const fetchTeamJerseyNumbers = (seasonId, leagueId, teamId) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/stats/team/players_jersey_numbers/?season=${seasonId}&league=${leagueId}&team=${teamId}`,
    method: 'GET',
    headers: getDefaultRequestHeaders(),
    types: [
      API_REQUEST_EMPTY_ACTION,
      {
        type: TEAM_PLAYERS_JERSEYS_SUCCESS,
        payload: (action, state, res) => {
          return getJSON(res)
            .then((json) => {
              const camelizedJson = camelizeKeys(json);
              const normalized = normalize(camelizedJson, schemas.PLAYER_ARRAY);

              return {
                entities: normalized.entities
              };
            });
        }
      },
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

const callUpdateDataAPI = (scoutReportId, payload) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/scout_reports/${scoutReportId}/`,
    method: 'PATCH',
    headers: getDefaultRequestHeaders(),
    body: JSON.stringify(payload),
    types: [
      API_REQUEST_EMPTY_ACTION,
      API_REQUEST_EMPTY_ACTION,
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const reorderGrid = (scoutReportId, grid, dispatch) => {
  dispatch(callUpdateDataAPI(scoutReportId, { players_grid: grid }));
  return {
    type: SCOUT_REPORT_REORDER_GRID,
    grid
  }
};

const callAddPlayerAPI = (scoutReportId, playerId) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/scout_reports/${scoutReportId}/players/`,
    method: 'POST',
    headers: getDefaultRequestHeaders(),
    body: JSON.stringify({
      player: playerId
    }),
    types: [
      API_REQUEST_EMPTY_ACTION,
      {
        type: SCOUT_REPORT_ADD_PLAYER_SUCCESS,
        payload: (action, state, res) => {
          return getJSON(res)
            .then((json) => {
              return {
                entities: {
                  players: {
                    [playerId]: {
                      content: json.content
                    }
                  }
                }
              };
            });
        }
      },
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const addPlayer = (scoutReportId, playerData, dispatch) => {
  dispatch(callAddPlayerAPI(scoutReportId, playerData.id));

  const camelizedJson = camelizeKeys(playerData);
  const normalized = normalize(camelizedJson, schemas.PLAYER);

  return {
    type: SCOUT_REPORT_ADD_PLAYER,
    payload: {
      playerId: playerData.id,
      entities: normalized.entities
    }
  }
};

const callRemovePlayerAPI = (scoutReportId, playerId) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/scout_reports/${scoutReportId}/players/${playerId}/`,
    method: 'DELETE',
    headers: getDefaultRequestHeaders(),
    types: [
      API_REQUEST_EMPTY_ACTION,
      API_REQUEST_EMPTY_ACTION,
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const removePlayer = (scoutReportId, playerId, dispatch) => {
  dispatch(callRemovePlayerAPI(scoutReportId, playerId));
  return {
    type: SCOUT_REPORT_REMOVE_PLAYER,
    playerId
  }
};

export const changeConfig = (scoutReportId, key, value, allConfig, dispatch) => {
  dispatch(callUpdateDataAPI(scoutReportId, { report_configuration: allConfig }));
  return {
    type: SCOUT_REPORT_CHANGE_CONFIGURATION,
    key,
    value
  }
};

export const changeContent = (scoutReportId, key, dbColumn, content, dispatch) => {
  dispatch(callUpdateDataAPI(scoutReportId, { [dbColumn]: content }));
  return {
    type: SCOUT_REPORT_CHANGE_CONTENT,
    key,
    content
  }
};

const callChangePlayerAPI = (scoutReportId, playerId, payload) => ({
  [CALL_API]: {
    endpoint: `${API_ROOT}/v1/scout_reports/${scoutReportId}/players/${playerId}/`,
    method: 'PATCH',
    headers: getDefaultRequestHeaders(),
    body: JSON.stringify(payload),
    types: [
      API_REQUEST_EMPTY_ACTION,
      API_REQUEST_EMPTY_ACTION,
      ...getDefaultAPIRequestErrorFSA()
    ]
  }
});

export const changePlayerContent = (scoutReportId, playerId, content, dispatch) => {
  dispatch(callChangePlayerAPI(scoutReportId, playerId, { content }));
  return {
    type: SCOUT_REPORT_CHANGE_PLAYER_CONTENT,
    payload: {
      entities: {
        players: {
          [playerId]: {
            content
          }
        }
      }
    }
  }
};

export const changePlayerConfiguration = (scoutReportId, playerId, playerConfiguration, dispatch) => {
  dispatch(callChangePlayerAPI(scoutReportId, playerId, {
    player_configuration: playerConfiguration
  }));
  return {
    type: SCOUT_REPORT_CHANGE_PLAYER_CONFIGURATION,
    playerId,
    playerConfiguration
  }
};

// Reducers

const scoutReportReducer = (state = Map({
  isFetching: false,
  data: Map()
}), action) => {
  switch (action.type) {
    case SCOUT_REPORT_REQUEST:
      return state.set('isFetching', true)
                  .set('data', Map());
    case SCOUT_REPORT_SUCCESS:
      return state.set('isFetching', false)
                  .set('data', fromJS(action.payload.data));
    case SCOUT_REPORT_REORDER_GRID:
      return state.setIn(['data', 'playersGrid'], fromJS(action.grid));
    case SCOUT_REPORT_ADD_PLAYER:
      return state.updateIn(['data', 'playersGrid', '1'], list =>
          list.push(action.payload.playerId)
        ).updateIn(['data', 'players'], list => list.push(Map({
          playerId: action.payload.playerId,
          playerConfiguration: null
        })));
    case SCOUT_REPORT_REMOVE_PLAYER:
      return state.updateIn(['data', 'players'], list => {  // remove from players
        let index = list.findIndex(p => (p.get('playerId') === action.playerId));
        if (index > -1) {
          return list.delete(index);
        }
        return list;
      }).updateIn(['data', 'playersGrid'], map => {  // remove from grid
        for (let i = 1; i <= 5; i++) {
          let index = map.get(i.toString()).indexOf(action.playerId);
          if (index > -1) {
            return map.updateIn(i.toString(), list => list.delete(index));
          }
        }
        return map;
      });
    case SCOUT_REPORT_CHANGE_PLAYER_CONFIGURATION:
      return state.updateIn(['data', 'players'], list => {  // remove from players
        let index = list.findIndex(p => (p.get('playerId') === action.playerId));
        if (index > -1) {
          return list.setIn([index, 'playerConfiguration'],
            Map(action.playerConfiguration));
        }
        return list;
      });
    case SCOUT_REPORT_CHANGE_CONFIGURATION:
      return state.updateIn(['data', 'reportConfiguration'], v => Map({
        ...(v ? v.toJS() : {}),
        [action.key]: action.value
      }));
    case SCOUT_REPORT_CHANGE_CONTENT:
      return state.updateIn([
        'data',
        action.key
      ], v => action.content);
    default:
      return state;
  }
};

export default scoutReportReducer;

// Selectors

const getStateScoutReport = (state) => state.scoutReport;

export const getScoutReport = createSelector(
  [
    getStateScoutReport,
    getStatePlayerEntities,
    getStatePlayerPositionEntities,
    getStateCountryEntities
  ],
  (scoutReport, playerEntities, playerPositionEntities, countryEntities) => {
    let report = scoutReport.toJS();
    report.playersData = {};

    if (scoutReport.hasIn(['data', 'players'])) {
      scoutReport.getIn(['data', 'players']).map((row, i) => {
        const playerId = row.get('playerId');
        report.playersData[playerId] =
          playerEntities.get(playerId.toString()).toJS();

        let mainNationality = report.playersData[playerId].mainNationality;
        if (mainNationality !== null) {
          mainNationality =
            countryEntities.get(mainNationality.toString()).toJS();
          report.playersData[playerId].mainNationality = mainNationality;
        }

        let position = report.playersData[playerId].position;
        if (position !== null) {
          position = playerPositionEntities.get(position.toString()).toJS();
          report.playersData[playerId].position = position;
        }

        report.playersData[playerId].playerConfiguration =
          row.get('playerConfiguration') ? row.get('playerConfiguration').toJS() : {};

        report.playersData[playerId].index = i;

        return true;
      });
    }

    return {
      isFetching: report.isFetching,
      ...report.data,
      playersData: report.playersData
    }
  }
);

export const getScoutReportHeader = createSelector(
  [ getSelectedSeason, getSelectedLeagues, getSelectedTeams,
    getStateSeasonEntities, getStateLeagueEntities, getStateTeamEntities ],
  (selectedSeason, selectedLeagues, selectedTeams,
    seasonEntities, leagueEntities, teamEntities) => {
    if (!selectedSeason || !selectedLeagues.length || !selectedTeams.length
      || !seasonEntities || !leagueEntities || !teamEntities
    ) {
      return {};
    }
    return {
      seasonName: seasonEntities.getIn([selectedSeason, 'name']),
      leagueName: leagueEntities.getIn([selectedLeagues[0], 'name']),
      teamName: teamEntities.getIn([selectedTeams[0], 'name']),
      teamLogo: teamEntities.getIn([selectedTeams[0], 'logo'])
    }
  }
);



// WEBPACK FOOTER //
// ./src/features/scout-report/duck.js